export const BASE = '/api/v1';

function getCurrentLang(): string {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('vianet-lang') || 'es';
  }
  return 'es';
}

/**
 * Token Bearer desde localStorage (fix cross-subdomain Vercel+Railway,
 * commit 571f88dd). Exportado para consumers que hacen `fetch()` manual
 * (multipart, streaming SSE, descarga blob) donde `request()` no se puede
 * usar por forzar `Content-Type: application/json`.
 */
export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('vianet-token');
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const lang = getCurrentLang();
  const token = getToken();
  const separator = path.includes('?') ? '&' : '?';
  const fullPath = `${BASE}${path}${separator}lang=${lang}`;
  const res = await fetch(fullPath, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-lang': lang,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; code?: string };
    };
    const err = new Error(body.error?.message ?? `Error ${res.status}`) as Error & {
      status?: number;
      code?: string;
    };
    err.status = res.status;
    if (body.error?.code) err.code = body.error.code;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// --- Archivos Adjuntos ---

export function fetchArchivos(codigoDocumento: string) {
  return request<{ data: import('../../../types').ArchivoAdjunto[] }>(
    `/documentos/${codigoDocumento}/archivos`,
  );
}

export function obtenerUrlArchivo(codigoDocumento: string, archivoId: string) {
  return request<{ data: { url: string } }>(`/documentos/${codigoDocumento}/archivos/${archivoId}`);
}

export async function subirArchivo(codigoDocumento: string, file: File, descripcion?: string) {
  const formData = new FormData();
  formData.append('archivo', file);
  if (descripcion) formData.append('descripcion', descripcion);

  const lang = getCurrentLang();
  const token = getToken();
  // Multipart: NO request() porque forzaría Content-Type: application/json y
  // rompería el boundary. Bearer manual para cross-subdomain Vercel+Railway.
  const res = await fetch(`${BASE}/documentos/${codigoDocumento}/archivos?lang=${lang}`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: {
      'x-lang': lang,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // NO Content-Type — browser sets multipart boundary
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `Error ${res.status}`);
  }

  return res.json() as Promise<{ data: import('../../../types').ArchivoAdjunto }>;
}

// --- Transcripción de audio (dictado de notas de madurez, prompt 0759) ---

export interface TranscripcionResultado {
  texto: string;
  idiomaDetectado?: string;
  duracionSegundos?: number;
  proveedor: string;
}

/**
 * Envía un clip de audio a POST /via/transcribir y devuelve el texto.
 * Multipart manual (mismo motivo que subirArchivo: request() forzaría JSON).
 */
export async function transcribirAudio(
  audio: Blob,
  nombreArchivo = 'nota.webm',
  idioma?: string,
): Promise<TranscripcionResultado> {
  const formData = new FormData();
  formData.append('audio', audio, nombreArchivo);
  if (idioma) formData.append('idioma', idioma);

  const lang = getCurrentLang();
  const token = getToken();
  const res = await fetch(`${BASE}/via/transcribir?lang=${lang}`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: {
      'x-lang': lang,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // NO Content-Type — el navegador fija el boundary multipart
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `Error ${res.status}`);
  }
  const json = (await res.json()) as { data: TranscripcionResultado };
  return json.data;
}

export function registrarEnlaceExterno(
  codigoDocumento: string,
  body: {
    nombreArchivo: string;
    enlaceExterno: string;
    mimeType?: string;
    descripcion?: string;
  },
) {
  return request<{ data: import('../../../types').ArchivoAdjunto }>(
    `/documentos/${codigoDocumento}/archivos/enlace`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function actualizarArchivo(
  codigoDocumento: string,
  archivoId: string,
  body: {
    nombreArchivo?: string;
    modoApertura?: string;
    descripcion?: string;
  },
) {
  return request<{ data: import('../../../types').ArchivoAdjunto }>(
    `/documentos/${codigoDocumento}/archivos/${archivoId}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

export function eliminarArchivo(codigoDocumento: string, archivoId: string) {
  return request<void>(`/documentos/${codigoDocumento}/archivos/${archivoId}`, {
    method: 'DELETE',
  });
}

// --- Compatibility mapping layer ---
// Backend uses hispanized field names (titulo, versionVigente, etc.)
// Frontend components still reference legacy aliases (nombre, edicionActual, empresa)
// This mapper adds those aliases so restored viewer components work without changes.

function mapDocCompat<T>(doc: T): T {
  const d = doc as Record<string, unknown>;
  return {
    ...doc,
    nombre: d.titulo ?? d.nombre,
    edicionActual: (d.versionVigente as Record<string, unknown>)?.numeroEdicion ?? 1,
    empresa: 'VALOR\u00B7IA Governance',
  } as T;
}

// --- Procesos ---

export function fetchProcesos() {
  return request<{ data: import('../../../types').Proceso[] }>('/procesos');
}

export function fetchProceso(codigo: string) {
  return request<{ data: import('../../../types').Proceso }>(`/procesos/${codigo}`);
}

// --- Documentos ---

export async function fetchDocumentos(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const res = await request<
    import('../../../types').PaginacionResponse<import('../../../types').DocumentoResumen>
  >(`/documentos${qs}`);
  return { ...res, datos: res.datos.map(mapDocCompat) };
}

export async function fetchDocumento(codigo: string) {
  const res = await request<{ data: import('../../../types').Documento }>(`/documentos/${codigo}`);
  return { data: mapDocCompat(res.data) };
}

export function crearDocumento(body: {
  procesoId: string;
  titulo: string;
  tipoDocumental: string;
  direccion?: string;
  tipoContenido?: import('../../../types').TipoContenido;
  enlaceExterno?: string;
  nombreArchivo?: string;
  mimeType?: string;
}) {
  return request<{ data: import('../../../types').DocumentoResumen }>('/documentos', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarDocumento(
  codigo: string,
  body: {
    titulo?: string;
    procesoCodigo?: string;
    direccion?: string;
  },
) {
  return request<{ data: import('../../../types').Documento }>(`/documentos/${codigo}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// --- Versiones ---

export function fetchVersiones(codigoDocumento: string) {
  return request<{ data: import('../../../types').DocumentVersion[] }>(
    `/versiones/documento/${codigoDocumento}`,
  );
}

export function actualizarContenido(versionId: string, contenidoJson: unknown) {
  return request<{ data: import('../../../types').DocumentVersion }>(
    `/versiones/${versionId}/contenido`,
    { method: 'PATCH', body: JSON.stringify({ contenidoJson }) },
  );
}

// Mapeo acciones UX legacy → workflow-engine unificado (ADR-025).
// La UX de docs sigue usando los nombres cortos (ENVIAR/APROBAR/RECHAZAR/PUBLICAR);
// el endpoint nuevo /transitar espera ENVIAR_REVISION/APROBAR/RECHAZAR/PUBLICAR.
const ACCION_LEGACY_A_WORKFLOW: Record<string, string> = {
  ENVIAR: 'ENVIAR_REVISION',
  APROBAR: 'APROBAR',
  RECHAZAR: 'RECHAZAR',
  PUBLICAR: 'PUBLICAR',
};

/**
 * Transiciona una versión de documento.
 *
 * 0629: por defecto usa el endpoint unificado `/versiones/:id/transitar` (motor
 * ADR-025) que delega al service legacy preservando la lógica de negocio
 * (firma SES, archivado, audit) y dispara Notify+Sign hooks unificados.
 *
 * Si el motor no soporta la acción (DESCARTAR/ARCHIVAR/REABRIR) cae al endpoint
 * legacy `/transicion` para no romper flujos existentes hasta 0633.
 */
export function transicionarEstado(versionId: string, accion: string, comentario?: string) {
  const accionUnificada = ACCION_LEGACY_A_WORKFLOW[accion];
  if (accionUnificada) {
    return request<{ data: import('../../../types').DocumentVersion }>(
      `/versiones/${versionId}/transitar`,
      { method: 'POST', body: JSON.stringify({ accion: accionUnificada, comentario }) },
    );
  }
  // Fallback legacy (acciones no soportadas por el motor todavía).
  return request<{ data: import('../../../types').DocumentVersion }>(
    `/versiones/${versionId}/transicion`,
    { method: 'POST', body: JSON.stringify({ accion, comentario }) },
  );
}

export function nuevaEdicion(codigoDocumento: string, descripcion: string) {
  return request<{ data: import('../../../types').DocumentVersion }>(
    `/versiones/documento/${codigoDocumento}/nueva-edicion`,
    { method: 'POST', body: JSON.stringify({ descripcion }) },
  );
}

// --- Pendientes (documents with versions in review/approval) ---

export async function fetchPendientes() {
  const res = await request<{
    datos: import('../../../types').DocumentoResumen[];
    paginacion: { total: number };
  }>('/documentos?estado=EN_REVISION,APROBADO&porPagina=50');
  return { ...res, datos: res.datos.map(mapDocCompat) };
}

// --- Permisos ---

export interface PermisosDocumento {
  canView: boolean;
  canReview: boolean;
  canApprove: boolean;
  userRole: string;
  userOrgRole: string | null;
  actions: string[];
  versionId: string | null;
  estadoVersion: string | null;
}

export function fetchPermisos(codigo: string) {
  return request<{ data: PermisosDocumento }>(`/documentos/${codigo}/permisos`);
}

// --- Busqueda ---

export function buscar(q: string, filtros?: { tipo?: string; estado?: string }) {
  const params = new URLSearchParams({ q });
  if (filtros?.tipo) params.set('tipo', filtros.tipo);
  if (filtros?.estado) params.set('estado', filtros.estado);
  return request<{ data: import('../../../types').DocumentoResumen[]; total: number }>(
    `/busqueda?${params}`,
  );
}

// --- Referencias ---

export function fetchReferencias() {
  return request<{ data: import('../../../types').ReferenciaCruzada[] }>('/referencias');
}

// --- Importar ---

export interface ScanResult {
  filename: string;
  filePath: string;
  code: string | null;
  processCode: string | null;
  docType: string | null;
  title: string;
  existsInDb: boolean;
  processFound: boolean;
  processName: string | null;
  canImport: boolean;
  reason: string | null;
}

export interface ImportResult {
  code: string;
  name: string;
  status: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
}

export interface ImportSummary {
  total: number;
  created: number;
  skipped: number;
  updated: number;
  errors: number;
}

export function escanearDirectorio(basePath: string) {
  return request<{ data: ScanResult[] }>('/importar/escanear', {
    method: 'POST',
    body: JSON.stringify({ basePath }),
  });
}

export function importarDocumentos(basePath: string) {
  return request<{ data: ImportResult[]; summary: ImportSummary }>('/importar', {
    method: 'POST',
    body: JSON.stringify({ basePath }),
  });
}

// --- Auditoría ---

export interface AuditLogEntry {
  id: string;
  tenantId: string;
  usuarioId: string;
  evento: string;
  entidad: string;
  entidadId: string;
  detalle: Record<string, unknown> | null;
  ip: string | null;
  creadoEn: string;
  usuario: { id: string; nombreCompleto: string; nombre?: string; email: string };
}

export interface AuditoriaResponse {
  data: AuditLogEntry[];
  total: number;
  page: number;
  totalPages: number;
}

export interface AuditoriaParams {
  entidad?: string;
  accion?: string;
  usuarioId?: string;
  desde?: string;
  hasta?: string;
  page?: number;
  limit?: number;
}

export function fetchAuditoria(params?: AuditoriaParams) {
  const qs = new URLSearchParams();
  if (params?.entidad) qs.set('entidad', params.entidad);
  if (params?.accion) qs.set('accion', params.accion);
  if (params?.usuarioId) qs.set('usuarioId', params.usuarioId);
  if (params?.desde) qs.set('desde', params.desde);
  if (params?.hasta) qs.set('hasta', params.hasta);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return request<AuditoriaResponse>(`/auditoria${query ? '?' + query : ''}`);
}

export function fetchAuditoriaEntidades() {
  return request<{ data: string[] }>('/auditoria/entidades');
}

export function fetchAuditoriaAcciones() {
  return request<{ data: string[] }>('/auditoria/acciones');
}

// --- Personas (V·IA Personas / M12) ---

export interface PersonaResumen {
  id: string;
  codigo: string;
  nombre: string;
  apellidos: string;
  email: string;
  telefono: string | null;
  fotoUrl: string | null;
  tipoPersona: 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO';
  estado: string;
  codigoDireccion: string | null;
  tipoContratoId?: string | null;
  categoriaLaboralId?: string | null;
  departamentoId?: string | null;
  secuenciaNumero?: number | null;
  asignacionesActivasCount?: number;
  puestoActual: {
    asignacionId: string;
    puesto: {
      id: string;
      titulo: string;
      nivel: string;
      departamento: { id: string; nombre: string } | null;
      organizacion: { id: string; nombre: string } | null;
    };
  } | null;
}

export interface PersonaDetalle {
  persona: PersonaResumen;
  puestoActual: {
    puesto: { id: string; titulo: string; nivel: string; codigo: string | null };
    departamento: { id: string; nombre: string } | null;
    organizacion: { id: string; nombre: string } | null;
  } | null;
  proyectosActivos: Array<{
    id: string;
    rol: string | null;
    dedicacion: number | null;
    fechaInicio: string;
    fechaFin: string | null;
    proyecto: { id: string; codigo: string; nombre: string; estado: string };
  }>;
  centros: Array<{
    id: string;
    esPrincipal: boolean;
    fechaInicio: string;
    fechaFin: string | null;
    centro: { id: string; codigo: string; nombre: string; ciudad: string | null };
  }>;
  rolesGobernanza: Array<{
    id: string;
    tipoRol: string;
    ambito: string | null;
    nombrePersonalizado: string | null;
  }>;
  historialPuestos: Array<{
    id: string;
    fte: number;
    esPrincipal: boolean;
    fechaInicio: string;
    fechaFin: string | null;
    activo: boolean;
    puesto: {
      id: string;
      titulo: string;
      nivel: string;
      departamento: { id: string; nombre: string } | null;
      organizacion: { id: string; nombre: string } | null;
    };
  }>;
  supervisorDerivado: {
    personaId: string;
    nombre: string;
    apellidos: string;
    codigoPuesto: string | null;
    tituloPuesto: string;
  } | null;
  paisCentroPrincipal: {
    id: string;
    nombre: string;
    codigoIso: string;
  } | null;
}

export interface PersonaEstadisticas {
  totalPersonas: number;
  empleados: number;
  colaboradores: number;
  externos: number;
  activos: number;
  bajas: number;
  sinPuesto: number;
  porDepartamento: Array<{ id: string; nombre: string; count: number }>;
}

export interface PersonaOrgChart {
  persona: { id: string; nombre: string; apellidos: string; fotoUrl: string | null };
  puesto: {
    id: string;
    titulo: string;
    nivel: string;
    departamento: { id: string; nombre: string } | null;
    organizacion: { id: string; nombre: string } | null;
  } | null;
  supervisor: {
    puesto: { id: string; titulo: string; nivel: string };
    persona: { id: string; nombre: string; apellidos: string; fotoUrl: string | null } | null;
  } | null;
  subordinados: Array<{
    puesto: { id: string; titulo: string; nivel: string };
    persona: { id: string; nombre: string; apellidos: string; fotoUrl: string | null } | null;
  }>;
}

export function fetchPersonas(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{
    data: PersonaResumen[];
    meta: { total: number; page: number; pageSize: number; totalPages: number };
  }>(`/apps/personas${qs}`);
}

export function fetchPersona(id: string) {
  return request<{ data: PersonaDetalle }>(`/apps/personas/${id}`);
}

export function fetchPersonaEstadisticas(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{ data: PersonaEstadisticas }>(`/apps/personas/estadisticas${qs}`);
}

export interface PersonaProcesosDocumentos {
  procesosRepresentados: Array<{
    id: string;
    codigo: string;
    nombre: string;
    tipologia: string;
    grupoNombre: string;
  }>;
  procesosResponsable: Array<{
    id: string;
    codigo: string;
    nombre: string;
    tipologia: string;
    grupoNombre: string;
    viasPuesto: string;
  }>;
  documentosCreados: Array<{
    id: string;
    codigo: string;
    titulo: string;
    estado: string;
    creadoEn: string;
  }>;
  documentosAprobados: Array<{
    id: string;
    codigo: string;
    titulo: string;
    fechaAprobacion: string;
  }>;
}

export function fetchPersonaProcesosDocumentos(personaId: string) {
  return request<{ data: PersonaProcesosDocumentos }>(
    `/apps/personas/${personaId}/procesos-documentos`,
  );
}

export function fetchPersonaOrganigrama(id: string) {
  return request<{ data: PersonaOrgChart }>(`/apps/personas/${id}/organigrama`);
}

export async function fetchPreviewCodigoPersona(
  tipoPersona: 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO',
): Promise<{ proximoCodigo: string; proximaSecuencia: number }> {
  const res = await request<{ data: { proximoCodigo: string; proximaSecuencia: number } }>(
    `/apps/personas/preview-codigo?tipoPersona=${tipoPersona}`,
  );
  return res.data;
}

// Proximo codigo autonumerado PU-NNNN tenant-wide · solo lectura (endpoint del 0453).
export async function fetchPreviewCodigoPuesto(): Promise<{
  proximoCodigo: string;
  proximaSecuencia: number;
}> {
  const res = await request<{ data: { proximoCodigo: string; proximaSecuencia: number } }>(
    '/puestos/preview-codigo',
  );
  return res.data;
}

// 0457 T-4 — Proximo codigo autonumerado RF-NNN tenant-wide · solo lectura. (actualizado 0473/0476)
// Simetrico con fetchPreviewCodigoPuesto. Endpoint registrado en roles-funcionales.routes.ts.
export async function fetchPreviewCodigoRolFuncional(): Promise<{
  proximoCodigo: string;
  proximaSecuencia: number;
}> {
  const res = await request<{ data: { proximoCodigo: string; proximaSecuencia: number } }>(
    '/roles-funcionales/preview-codigo',
  );
  return res.data;
}

// Actualiza la relacion jerarquica padre de un puesto (0454 M3).
// Cierra la relacion vigente (fechaFin=today, esPrincipal) y crea nueva si puestoPadreId != null.
export async function actualizarOrganigramaPuesto(
  puestoId: string,
  body: { puestoPadreId: string | null },
): Promise<{
  supervisorDerivado: { nombre: string; apellidos: string } | null;
  padre: { id: string; titulo: string; codigo: string | null } | null;
}> {
  return request<{
    supervisorDerivado: { nombre: string; apellidos: string } | null;
    padre: { id: string; titulo: string; codigo: string | null } | null;
  }>(`/puestos/${puestoId}/organigrama`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function crearPersona(body: {
  codigo: string;
  nombre: string;
  apellidos: string;
  email: string;
  telefono?: string;
  tipoPersona: 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO';
  fechaAlta?: string;
  tipoContrato?: string;
  categoriaLaboral?: string;
  codigoDireccion?: string;
}) {
  return request<{ data: PersonaResumen }>('/apps/personas', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarPersona(id: string, body: Record<string, unknown>) {
  return request<{ data: PersonaResumen }>(`/apps/personas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function darDeBajaPersona(id: string) {
  return request<{ data: PersonaResumen }>(`/apps/personas/${id}`, {
    method: 'DELETE',
  });
}

// Persona sub-resources

export interface AsignacionItem {
  id: string;
  puestoId?: string;
  personaId?: string;
  fechaInicio: string;
  fechaFin?: string | null;
  activo: boolean;
  fte?: number;
  esPrincipal?: boolean;
  puesto?: {
    id: string;
    titulo: string;
    nivel?: string;
    departamento?: { id: string; nombre: string } | null;
    organizacion?: { id: string; nombre: string } | null;
  };
}

export interface ProyectoAsignacionItem {
  id: string;
  rol?: string | null;
  dedicacion?: number | null;
  fechaInicio?: string;
  proyecto?: { id: string; codigo: string; nombre: string; estado: string };
}

export interface CentroAsignacionItem {
  id: string;
  esPrincipal?: boolean;
  fechaInicio?: string;
  centro?: { id: string; codigo: string; nombre: string; ciudad?: string | null };
}

export function fetchPersonaAsignaciones(personaId: string) {
  return request<{ data: AsignacionItem[] }>(`/apps/personas/${personaId}/asignaciones`);
}

export function crearPersonaAsignacion(
  personaId: string,
  body: {
    puestoId: string;
    fte?: number;
    esPrincipal?: boolean;
    fechaInicio: string;
    fechaFin?: string;
  },
) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/asignaciones`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function finalizarPersonaAsignacion(personaId: string, asignacionId: string) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/asignaciones/${asignacionId}`, {
    method: 'DELETE',
  });
}

export function fetchPersonaProyectos(personaId: string) {
  return request<{ data: ProyectoAsignacionItem[] }>(`/apps/personas/${personaId}/proyectos`);
}

export function asignarPersonaProyecto(
  personaId: string,
  body: {
    proyectoId: string;
    rol?: string;
    dedicacion?: number;
    fechaInicio: string;
  },
) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/proyectos`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function desasignarPersonaProyecto(personaId: string, asignacionId: string) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/proyectos/${asignacionId}`, {
    method: 'DELETE',
  });
}

export function fetchPersonaCentros(personaId: string) {
  return request<{ data: CentroAsignacionItem[] }>(`/apps/personas/${personaId}/centros`);
}

export function asignarPersonaCentro(
  personaId: string,
  body: {
    centroId: string;
    fechaInicio: string;
    esPrincipal?: boolean;
  },
) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/centros`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function desasignarPersonaCentro(personaId: string, asignacionId: string) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/centros/${asignacionId}`, {
    method: 'DELETE',
  });
}

// Structure endpoints (for personas filters)
export function fetchDepartamentos(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{
    data: Array<{ id: string; nombre: string; codigo: string; organizacionId?: string }>;
  }>(`/departamentos${qs}`);
}

export function fetchDepartamento(id: string) {
  return request<{ data: unknown }>(`/departamentos/${id}`);
}

export function crearDepartamento(body: Record<string, unknown>) {
  return request<{ data: unknown }>('/departamentos', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarDepartamento(id: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/departamentos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarDepartamento(id: string) {
  return request<void>(`/departamentos/${id}`, { method: 'DELETE' });
}

// --- Organizaciones (M1 Structure) ---

export interface OrganizacionResumen {
  id: string;
  codigo: string;
  nombre: string;
  nombreCorto: string | null;
  tipo: string;
  activo: boolean;
  _count?: { departamentos: number; puestos: number };
  departamentos?: Array<{ id: string; nombre: string; codigo?: string }>;
}

export function fetchOrganizaciones() {
  return request<{ data: OrganizacionResumen[] }>('/organizaciones');
}

export interface CentroResumen {
  id: string;
  codigo: string;
  nombre: string;
  ciudad?: string | null;
  organizacionId?: string;
  activo?: boolean;
}

export function fetchCentros(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{ data: CentroResumen[] }>(`/apps/centros${qs}`);
}

export interface ProyectoResumen {
  id: string;
  codigo: string;
  nombre: string;
  estado?: string;
}

export function fetchProyectos(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{ data: ProyectoResumen[] }>(`/apps/proyectos${qs}`);
}

export interface MotivoCambioEstado {
  id: string;
  codigo: string | null;
  nombre: string;
}

export interface HistorialEstadoItem {
  id: string;
  estadoAnterior: string;
  estadoNuevo: string;
  fechaCambio: string;
  observaciones: string | null;
  creadoEn: string;
  motivo: { id: string; codigo: string | null; nombre: string } | null;
  cambiadoPor: { id: string; nombreCompleto: string; email: string } | null;
}

export function fetchMotivosCambioEstado() {
  return request<{ data: MotivoCambioEstado[] }>('/catalogos-tenant?tipo=MotivoCambioEstado');
}

export function fetchHistorialEstadoPersona(personaId: string) {
  return request<{ data: HistorialEstadoItem[] }>(`/apps/personas/${personaId}/historial-estado`);
}

export interface CambioEstadoBody {
  estadoNuevo: 'ACTIVO' | 'BAJA' | 'SUSPENDIDO';
  motivoId?: string;
  fechaCambio: string;
  observaciones?: string;
}

export function postCambioEstadoPersona(personaId: string, body: CambioEstadoBody) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/cambio-estado`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchOrganizacion(id: string) {
  return request<{
    data: OrganizacionResumen & {
      organizacionPadre?: OrganizacionResumen | null;
      hijas?: OrganizacionResumen[];
    };
  }>(`/organizaciones/${id}`);
}

export function crearOrganizacion(body: Record<string, unknown>) {
  return request<{ data: unknown }>('/organizaciones', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarOrganizacion(id: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/organizaciones/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarOrganizacion(id: string) {
  return request<void>(`/organizaciones/${id}`, { method: 'DELETE' });
}

// --- Puestos ---

export interface PuestoItem {
  id: string;
  titulo: string;
  codigo?: string | null;
  nivel?: string;
  // 0474: nivelPuestoId es el FK UUID real; nivel (slug) queda como legacy/deprecated.
  nivelPuestoId?: string | null;
  nivelPuesto?: {
    id: string;
    codigo?: string | null;
    nombre: string;
    nombreCorto?: string | null;
    color?: string | null;
    colorFondo?: string | null;
    orden?: number | null;
  } | null;
  rolFuncionalId?: string | null;
  departamentoId?: string | null;
  superiorId?: string | null;
  organizacionId?: string;
  activo?: boolean;
  departamento?: { id: string; nombre: string } | null;
  organizacion?: { id: string; nombre: string } | null;
  rolFuncional?: { id: string; nombre: string; fichaDocumentoId: string | null } | null;
  asignaciones?: Array<{
    activo: boolean;
    fechaFin?: string | null;
    persona?: { nombre: string; apellidos: string } | null;
  }>;
  reportaA?: Array<{
    puestoPadre: { id: string; titulo: string; codigo?: string | null };
  }>;
  supervisa?: Array<{
    puestoHijo: { id: string; titulo: string; codigo?: string | null };
  }>;
  rolesGobernanza?: Array<{
    id: string;
    tipoRol: string;
    nombrePersonalizado?: string | null;
    ambito?: string | null;
  }>;
}

export function fetchPuestos(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{
    data: PuestoItem[];
    datos?: PuestoItem[];
    meta?: { total: number; totalPages: number };
  }>(`/puestos${qs}`);
}

export function fetchPuesto(id: string) {
  return request<{ data: PuestoItem }>(`/puestos/${id}`);
}

export function crearPuesto(body: Record<string, unknown>) {
  return request<{ data: { id: string } }>('/puestos', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarPuesto(id: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/puestos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarPuesto(id: string) {
  return request<void>(`/puestos/${id}`, { method: 'DELETE' });
}

export function fetchCadenaMando(puestoId: string) {
  return request<{ data: PuestoItem[] }>(`/puestos/${puestoId}/cadena`);
}

export function fetchSubordinados(puestoId: string) {
  return request<{ data: { hijos?: PuestoItem[]; subordinados?: PuestoItem[] } | PuestoItem[] }>(
    `/puestos/${puestoId}/subordinados`,
  );
}

export function crearFichaPuesto(puestoId: string) {
  return request<{ data: unknown }>(`/puestos/${puestoId}/ficha`, { method: 'POST' });
}

export function fetchFichaPuesto(puestoId: string) {
  return request<{ data: unknown }>(`/puestos/${puestoId}/ficha`);
}

export interface FichaContenido {
  codigo: string;
  titulo: string;
  estado: string;
  contenidoJson: ProseMirrorDoc;
  /** Prompt 368: IDs expuestos para workflow (transicion de estado + firma) */
  documentoId?: string;
  versionId?: string;
}

interface ProseMirrorDoc {
  type: string;
  content: ProseMirrorNode[];
}

interface ProseMirrorNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: ProseMirrorNode[];
  text?: string;
  marks?: Array<{ type: string }>;
}

export function fetchFichaContenido(puestoId: string) {
  return request<{ data: FichaContenido }>(`/puestos/${puestoId}/ficha/contenido`);
}

// 0454 M4 · Cada seccion puede ser string plano (legacy, un paragraph) o array de nodos
// ProseMirror (Tiptap con marks/color/align/listas). El backend acepta ambos formatos.
export type FichaSeccionContenido = string | unknown[];

export interface FichaSeccionesInput {
  mision?: FichaSeccionContenido;
  funciones?: FichaSeccionContenido;
  competenciasTecnicas?: FichaSeccionContenido;
  competenciasTransversales?: FichaSeccionContenido;
  formacion?: FichaSeccionContenido;
  experiencia?: FichaSeccionContenido;
  certificaciones?: FichaSeccionContenido;
  condiciones?: FichaSeccionContenido;
}

export function actualizarFichaContenido(puestoId: string, secciones: FichaSeccionesInput) {
  return request<{ data: { ok: boolean } }>(`/puestos/${puestoId}/ficha/contenido`, {
    method: 'PUT',
    body: JSON.stringify(secciones),
  });
}

// Prompt 368: Batch creation de Fichas de Puesto
export interface FichaBatchResultFP {
  created: Array<{ id: string; codigo: string; puestoId: string }>;
  skipped: Array<{ puestoId: string; motivo: string }>;
  errors: Array<{ puestoId: string; message: string; code?: string }>;
}

export function crearFichaPuestoBatch(puestoIds: string[]) {
  return request<{ data: FichaBatchResultFP }>(`/puestos/ficha/batch`, {
    method: 'POST',
    body: JSON.stringify({ puestoIds }),
  });
}

// Prompt 368: Transicion de estado de version (workflow BORRADOR → EN_REVISION → APROBADO → PUBLICADO)
export function transicionarEstadoVersion(versionId: string, nuevoEstado: string) {
  return request<{ data: { ok: boolean } }>(`/versiones/${versionId}/transicion`, {
    method: 'POST',
    body: JSON.stringify({ nuevoEstado }),
  });
}

// Prompt 369: Iniciar firma M15 Sign para una versión de documento (FP/FR).
// entidadTipo fijo a 'version_documento' — ficha = versión documental.
export function solicitarAprobacionSign(params: { entidadId: string; titulo: string }) {
  return request<{ data: { ok: boolean; notificadosCount?: number } }>(
    `/sign/solicitar-aprobacion`,
    {
      method: 'POST',
      body: JSON.stringify({
        entidadTipo: 'version_documento',
        entidadId: params.entidadId,
        titulo: params.titulo,
      }),
    },
  );
}

// --- Organigrama ---

export interface OrganigramaNode {
  id: string;
  type?: string;
  position?: { x: number; y: number };
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface OrganigramaEdge {
  id: string;
  source: string;
  target: string;
  [key: string]: unknown;
}

export async function fetchOrganigramaArbol(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const res = await request<{ data: { nodes: OrganigramaNode[]; edges: OrganigramaEdge[] } }>(
    `/organigrama/arbol${qs}`,
  );
  return res.data;
}

export async function fetchOrganigramaZoom(puestoId: string, profundidad?: number) {
  const qs = profundidad != null ? `?profundidad=${profundidad}` : '';
  const res = await request<{ data: { nodes: OrganigramaNode[]; edges: OrganigramaEdge[] } }>(
    `/organigrama/zoom/${puestoId}${qs}`,
  );
  return res.data;
}

// --- Gobernanza ---

export function fetchVacantes() {
  return request<{ data: unknown[] }>('/gobernanza/vacantes');
}

export function fetchMatrizResponsabilidad() {
  return request<{ data: unknown }>('/gobernanza/matriz-responsabilidad');
}

export function fetchAnalisisImpacto(puestoId: string) {
  return request<{ data: unknown }>(`/gobernanza/analisis-impacto/${puestoId}`);
}

// --- Niveles de Puesto (configurable) ---

export interface NivelPuesto {
  id: string;
  codigo: string;
  // 0472: slug semantico estable (C_LEVEL, DIRECTOR, STAFF...) usado por la logica de aprobaciones.
  slug?: string;
  nombre: string;
  nombreCorto: string | null;
  orden: number;
  color: string | null;
  colorFondo: string | null;
  activo: boolean;
}

export function fetchNivelesPuesto() {
  return request<{ data: NivelPuesto[] }>('/niveles-puesto');
}

export function crearNivelPuesto(body: {
  codigo: string;
  nombre: string;
  nombreCorto?: string;
  orden?: number;
  color?: string;
  colorFondo?: string;
}) {
  return request<{ data: NivelPuesto }>('/niveles-puesto', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarNivelPuesto(id: string, body: Record<string, unknown>) {
  return request<{ data: NivelPuesto }>(`/niveles-puesto/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarNivelPuesto(id: string) {
  return request<{ data: NivelPuesto }>(`/niveles-puesto/${id}`, { method: 'DELETE' });
}

// --- Tipos de Contrato ---

export interface TipoContrato {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
}

export function fetchTiposContrato(incluirInactivos = false) {
  const qs = incluirInactivos ? '?incluirInactivos=true' : '';
  return request<{ data: TipoContrato[] }>(`/tipos-contrato${qs}`);
}

export function crearTipoContrato(body: {
  nombre: string;
  descripcion?: string;
  activo?: boolean;
}) {
  return request<{ data: TipoContrato }>('/tipos-contrato', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarTipoContrato(id: string, body: Record<string, unknown>) {
  return request<{ data: TipoContrato }>(`/tipos-contrato/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarTipoContrato(id: string) {
  return request<{ data: TipoContrato }>(`/tipos-contrato/${id}`, { method: 'DELETE' });
}

// --- Categorias Laborales ---

export interface CategoriaLaboral {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
}

export function fetchCategoriasLaborales(incluirInactivos = false) {
  const qs = incluirInactivos ? '?incluirInactivos=true' : '';
  return request<{ data: CategoriaLaboral[] }>(`/categorias-laborales${qs}`);
}

export function crearCategoriaLaboral(body: {
  nombre: string;
  descripcion?: string;
  activo?: boolean;
}) {
  return request<{ data: CategoriaLaboral }>('/categorias-laborales', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarCategoriaLaboral(id: string, body: Record<string, unknown>) {
  return request<{ data: CategoriaLaboral }>(`/categorias-laborales/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarCategoriaLaboral(id: string) {
  return request<{ data: CategoriaLaboral }>(`/categorias-laborales/${id}`, { method: 'DELETE' });
}

// --- Tipos de Unidad Organizativa ---

export interface TipoUnidadOrganizativa {
  id: string;
  codigo: string;
  nombre: string;
  nombreCorto: string | null;
  orden: number;
  color: string | null;
  esDefault: boolean;
  activo: boolean;
  _count?: { departamentos: number };
}

export function fetchTiposUnidadOrg(incluirInactivos = false) {
  const qs = incluirInactivos ? '?incluirInactivos=true' : '';
  return request<{ data: TipoUnidadOrganizativa[] }>(`/tipos-unidad-organizativa${qs}`);
}

export function crearTipoUnidadOrganizativa(body: {
  nombre: string;
  nombreCorto?: string;
  orden: number;
  color?: string;
}) {
  return request<{ data: TipoUnidadOrganizativa }>('/tipos-unidad-organizativa', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarTipoUnidadOrganizativa(id: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/tipos-unidad-organizativa/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarTipoUnidadOrganizativa(id: string) {
  return request<{ data: unknown }>(`/tipos-unidad-organizativa/${id}`, { method: 'DELETE' });
}

// --- Roles Funcionales ---

export interface RolFuncional {
  id: string;
  // 0457 T-4 — codigo autogenerado RF-NNN tenant-wide (nullable durante backfill). (actualizado 0473/0476)
  codigo: string | null;
  secuenciaNumero?: number | null;
  nombre: string;
  descripcion: string | null;
  nivelMinimo: string | null;
  nivelMaximo: string | null;
  fichaDocumentoId: string | null;
  activo: boolean;
  _count?: { puestos: number };
}

export function fetchRolesFuncionales() {
  return request<{ data: RolFuncional[] }>('/roles-funcionales');
}

// F2.3 (prompt 347) — Ficha de Rol Funcional (documento ProseMirror tipo FR)
export function crearFichaRolFuncional(rolId: string) {
  return request<{ data: { id: string; codigo: string; titulo: string; estado: string } }>(
    `/roles-funcionales/${rolId}/ficha`,
    { method: 'POST' },
  );
}

export function fetchFichaRolFuncionalContenido(rolId: string) {
  return request<{ data: FichaContenido }>(`/roles-funcionales/${rolId}/ficha/contenido`);
}

export function actualizarFichaRolFuncionalContenido(
  rolId: string,
  secciones: FichaSeccionesInput,
) {
  return request<{ data: { ok: boolean } }>(`/roles-funcionales/${rolId}/ficha/contenido`, {
    method: 'PUT',
    body: JSON.stringify(secciones),
  });
}

// Prompt 368: Batch creation de Fichas de Rol Funcional
export interface FichaBatchResultFR {
  created: Array<{ id: string; codigo: string; rolId: string }>;
  skipped: Array<{ rolId: string; motivo: string }>;
  errors: Array<{ rolId: string; message: string; code?: string }>;
}

export function crearFichaRolFuncionalBatch(rolIds: string[]) {
  return request<{ data: FichaBatchResultFR }>(`/roles-funcionales/ficha/batch`, {
    method: 'POST',
    body: JSON.stringify({ rolIds }),
  });
}

export function crearRolFuncional(body: {
  nombre: string;
  descripcion?: string;
  nivelMinimo?: string;
  nivelMaximo?: string;
}) {
  return request<{ data: RolFuncional }>('/roles-funcionales', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarRolFuncional(id: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/roles-funcionales/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function desactivarRolFuncional(id: string) {
  return request<{ data: unknown }>(`/roles-funcionales/${id}`, { method: 'DELETE' });
}

// --- Roles de Gobernanza (admin) [prompt 0470: catalogo por tenant] ----------
//
// Desde 0470 el tipo vive en TipoRolGobernanzaDef (tabla STR.tipos_rol_gobernanza)
// por tenant. `RolGobernanza` referencia al catalogo via tipoRolId (uuid).

// Tipo legacy `TipoRolGobernanza` mantenido como alias string para consumer
// shared/*; las APIs nuevas usan tipoRolId (uuid) + tipoRolDefinicion.codigo.
export type TipoRolGobernanza = string;

export interface TipoRolGobernanzaDefRef {
  id: string;
  codigo: string | null;
  /** RG-NNN correlativo visual por tenant (0471.1a). Autogenerado en INSERT. */
  numeroCorrelativo: string;
  nombre: string;
  esSistema: boolean;
  esPorProceso: boolean;
}

export interface TipoRolGobernanzaAdmin extends TipoRolGobernanzaDefRef {
  descripcion: string | null;
  fichaDocumentoId: string | null;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
  _count?: { roles: number };
}

export interface TipoRolGobernanzaDetalle extends TipoRolGobernanzaAdmin {
  roles: Array<{
    id: string;
    puestoId: string;
    procesoId: string | null;
    ambito: string | null;
    nombrePersonalizado: string | null;
    puesto: {
      id: string;
      titulo: string;
      codigo: string | null;
      asignaciones: Array<{
        persona: { id: string; nombre: string; apellidos: string } | null;
      }>;
    };
    proceso: { id: string; codigo: string; nombre: string } | null;
  }>;
}

export interface CrearTipoRolGobernanzaBody {
  nombre: string;
  descripcion?: string;
}

export interface ActualizarTipoRolGobernanzaBody {
  nombre?: string;
  descripcion?: string | null;
  activo?: boolean;
}

export function fetchTiposRolGobernanza(opts?: { incluirInactivos?: boolean }) {
  const qs = opts?.incluirInactivos ? '?incluirInactivos=true' : '';
  return request<{ data: TipoRolGobernanzaAdmin[] }>(`/tipos-rol-gobernanza${qs}`);
}

export function fetchTipoRolGobernanza(id: string) {
  return request<{ data: TipoRolGobernanzaDetalle }>(`/tipos-rol-gobernanza/${id}`);
}

/** 0642b: lista puestos asignados a un tipo de rol gobernanza. */
export function fetchPuestosDeTipoRol(tipoRolId: string) {
  return request<{ data: Array<{ id: string; codigo: string | null; titulo: string }> }>(
    `/tipos-rol-gobernanza/${tipoRolId}/puestos`,
  );
}

export function crearTipoRolGobernanza(body: CrearTipoRolGobernanzaBody) {
  return request<{ data: TipoRolGobernanzaAdmin }>('/tipos-rol-gobernanza', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarTipoRolGobernanza(id: string, body: ActualizarTipoRolGobernanzaBody) {
  return request<{ data: TipoRolGobernanzaAdmin }>(`/tipos-rol-gobernanza/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function desactivarTipoRolGobernanza(id: string) {
  return request<{ data: TipoRolGobernanzaAdmin }>(`/tipos-rol-gobernanza/${id}`, {
    method: 'DELETE',
  });
}

export function fetchAuditoriaTipoRolGobernanza(id: string, opts?: { limit?: number }) {
  const qs = opts?.limit ? `?limit=${opts.limit}` : '';
  return request<{ data: AuditoriaEntry[] }>(`/tipos-rol-gobernanza/${id}/auditoria${qs}`);
}

// --- Asignaciones (RolGobernanza) --------------------------------------------

export interface RolGobernanzaAdmin {
  id: string;
  tipoRolId: string;
  tipoRolDefinicion?: TipoRolGobernanzaDefRef | null;
  /** @deprecated usar tipoRolDefinicion.codigo */
  tipoRol?: string | null;
  nombrePersonalizado: string | null;
  ambito: string | null;
  activo: boolean;
  puestoId: string;
  procesoId?: string | null;
  puesto?: {
    id: string;
    titulo: string;
    codigo?: string | null;
    nivelPuesto?: {
      id: string;
      nombre: string;
      color: string | null;
      colorFondo: string | null;
    } | null;
    departamento?: { id: string; nombre: string } | null;
    asignaciones?: Array<{
      persona: { id: string; nombre: string; apellidos: string | null };
    }>;
  } | null;
}

export function fetchRolesGobernanza(opts?: { incluirInactivos?: boolean }) {
  const qs = opts?.incluirInactivos ? '?incluirInactivos=true' : '';
  return request<{ data: RolGobernanzaAdmin[] }>(`/roles-gobernanza${qs}`);
}

export interface CrearRolGobernanzaBody {
  puestoId: string;
  tipoRolId: string;
  procesoId?: string;
  ambito?: string | null;
  nombrePersonalizado?: string | null;
}

export interface ActualizarRolGobernanzaBody {
  puestoId?: string;
  ambito?: string | null;
  nombrePersonalizado?: string | null;
  activo?: boolean;
}

export function crearRolGobernanza(body: CrearRolGobernanzaBody) {
  return request<{ data: RolGobernanzaAdmin }>('/roles-gobernanza', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarRolGobernanza(id: string, body: ActualizarRolGobernanzaBody) {
  return request<{ data: RolGobernanzaAdmin }>(`/roles-gobernanza/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function desactivarRolGobernanza(id: string) {
  return request<{ data: RolGobernanzaAdmin }>(`/roles-gobernanza/${id}`, { method: 'DELETE' });
}

// Ficha FG (documento ProseMirror) — un documento por tipoRolId (catalogo).
export interface FichaRolGobernanzaRef {
  id: string;
  codigo: string;
  titulo: string;
  estado: string;
  versionVigenteId: string | null;
}

export interface FichaRolGobernanzaContenido {
  codigo: string;
  titulo: string;
  estado: string;
  contenidoJson: unknown;
  documentoId: string;
  versionId: string;
}

export function fetchFichaRolGobernanza(tipoRolId: string) {
  return request<{ data: FichaRolGobernanzaRef }>(
    `/roles-gobernanza/fichas/${encodeURIComponent(tipoRolId)}`,
  );
}

export function fetchFichaRolGobernanzaContenido(tipoRolId: string) {
  return request<{ data: FichaRolGobernanzaContenido }>(
    `/roles-gobernanza/fichas/${encodeURIComponent(tipoRolId)}/contenido`,
  );
}

export interface FichaFGSecciones {
  mision?: string;
  funciones?: string[];
  competencias?: string;
  relaciones?: string;
  indicadores?: string;
}

export function crearFichaRolGobernanza(tipoRolId: string, secciones?: FichaFGSecciones) {
  return request<{ data: { id: string } }>(
    `/roles-gobernanza/fichas/${encodeURIComponent(tipoRolId)}`,
    {
      method: 'POST',
      body: JSON.stringify(secciones ?? {}),
    },
  );
}

export function actualizarFichaRolGobernanzaContenido(
  tipoRolId: string,
  secciones: FichaFGSecciones,
) {
  return request<{ data: { ok: boolean } }>(
    `/roles-gobernanza/fichas/${encodeURIComponent(tipoRolId)}/contenido`,
    {
      method: 'PUT',
      body: JSON.stringify(secciones),
    },
  );
}

// Auditoria del rol de gobernanza — lista de eventos CREAR/ACTUALIZAR/ELIMINAR.
export type AccionAuditoria =
  | 'CREAR'
  | 'ACTUALIZAR'
  | 'ELIMINAR'
  | 'APROBAR'
  | 'RECHAZAR'
  | 'PUBLICAR'
  | 'ARCHIVAR'
  | 'EXPORTAR'
  | 'OBSOLETO'
  | 'LOGIN';

export interface AuditoriaEntry {
  id: string;
  accion: AccionAuditoria;
  creadoEn: string;
  usuario: { id: string; nombreCompleto: string; email: string } | null;
  datosAnteriores: unknown;
  datosNuevos: unknown;
}

export function fetchAuditoriaRolGobernanza(rolId: string, opts?: { limit?: number }) {
  const qs = opts?.limit ? `?limit=${opts.limit}` : '';
  return request<{ data: AuditoriaEntry[] }>(`/roles-gobernanza/${rolId}/auditoria${qs}`);
}

// --- Catalogos Tenant ---

export interface CatalogoTenantItem {
  id: string;
  ambito: string;
  tipo: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  orden: number;
}

export function fetchCatalogosTenant(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{ data: CatalogoTenantItem[] }>(`/catalogos-tenant${qs}`);
}

export function crearCatalogoTenant(body: Record<string, unknown>) {
  return request<{ data: CatalogoTenantItem }>('/catalogos-tenant', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarCatalogoTenant(id: string, body: Record<string, unknown>) {
  return request<{ data: CatalogoTenantItem }>(`/catalogos-tenant/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarCatalogoTenant(id: string) {
  return request<void>(`/catalogos-tenant/${id}`, { method: 'DELETE' });
}

// --- Analytics Gobernanza ---

export interface GobernanzaAnalyticsKpis {
  totalPersonas: number;
  totalPuestos: number;
  vacantes: number;
  rolesActivos: number;
  puestosConRol: number;
  busFactor: number;
  fichasPuesto: number;
  vacantesCriticas: number;
}

export interface GobernanzaRolDetalle {
  id: string;
  tipoRol: string;
  ambito: string | null;
  nombrePersonalizado: string | null;
  puestoTitulo?: string;
}

export interface GobernanzaConcentracion {
  personaId: string;
  nombre: string;
  apellidos: string;
  codigo: string;
  numRoles: number;
  roles: GobernanzaRolDetalle[];
}

export interface GobernanzaRolPorTipo {
  tipo: string;
  count: number;
  color: string;
}

export interface GobernanzaCoberturaNivel {
  nivel: string;
  total: number;
  conRol: number;
  porcentaje: number;
}

export interface GobernanzaVacanteDetalle {
  puestoId: string;
  titulo: string;
  codigo: string | null;
  nivel: string;
  departamento: { id: string; nombre: string } | null;
  roles: Array<{
    id: string;
    tipoRol: string;
    ambito: string | null;
    nombrePersonalizado: string | null;
  }>;
  esCritica: boolean;
}

export interface GobernanzaAlerta {
  tipo: string;
  severidad: 'critico' | 'alto' | 'medio' | 'bajo';
  mensaje: string;
  datos?: unknown;
}

export interface GobernanzaPersonaRow {
  id: string;
  nombre: string;
  apellidos: string;
  codigo: string;
  numRoles: number;
  roles: GobernanzaRolDetalle[];
}

export interface GobernanzaAnalytics {
  kpis: GobernanzaAnalyticsKpis;
  concentracionRoles: GobernanzaConcentracion[];
  rolesPorTipo: GobernanzaRolPorTipo[];
  coberturaPorNivel: GobernanzaCoberturaNivel[];
  vacantesDetalle: GobernanzaVacanteDetalle[];
  alertas: GobernanzaAlerta[];
  personas: GobernanzaPersonaRow[];
}

export function fetchGobernanzaAnalytics() {
  return request<{ data: GobernanzaAnalytics }>('/structure/analytics/gobernanza');
}

// --- Analytics Negocio (Structure M1) ---

export interface NegocioAnalyticsKpis {
  totalPersonas: number;
  totalEmpresas: number;
  totalCentros: number;
  totalPuestos: number;
  totalPaises: number;
  totalDirecciones: number;
  altasUltimos3Meses: number;
}

export interface NegocioEvolucionMensual {
  mes: string;
  mesLabel: string;
  altas: number;
  acumulado: number;
}

export interface NegocioNameValue {
  name: string;
  value: number;
}

export interface NegocioPersonaRow {
  id: string;
  nombre: string;
  apellidos: string;
  tipoPersona: string;
  puesto: string | null;
  direccion: string | null;
  empresa: string | null;
  centro: string | null;
  pais: string | null;
  superior: string | null;
  superiorId: string | null;
}

export interface NegocioAnalytics {
  kpis: NegocioAnalyticsKpis;
  evolucionMensual: NegocioEvolucionMensual[];
  porDireccion: NegocioNameValue[];
  porPuesto: NegocioNameValue[];
  porEmpresa: NegocioNameValue[];
  porCentro: NegocioNameValue[];
  porPais: NegocioNameValue[];
  personas: NegocioPersonaRow[];
}

export function fetchNegocioAnalytics() {
  return request<{ data: NegocioAnalytics }>('/structure/analytics/negocio');
}

// --- M1.5 Madurez de Estructura (VALOR·IA 5D) ---

export type NivelMadurez = 'M_0' | 'M_1' | 'M_2' | 'M_3' | 'M_4' | 'M_5';
export type DimensionMadurez =
  | 'FORMALIZACION'
  | 'GOBERNABILIDAD'
  | 'ALINEACION_ESTRATEGICA'
  | 'CAPACIDAD_CONTROL'
  | 'CAPACIDAD_EVOLUCION';
export type ColorMadurez = 'VERDE' | 'AMARILLO' | 'ROJO';
export type EstadoMadurez = 'BORRADOR' | 'EN_REVISION' | 'APROBADA' | 'OBSOLETA';
export type TipoEjercicioMadurez =
  | 'DIAGNOSTICO_INICIAL'
  | 'EVALUACION_PERIODICA'
  | 'POST_TRANSFORMACION'
  | 'AUDITORIA_EXTERNA';

export interface MadurezEvaluacion {
  id: string;
  codigo: string | null;
  tipoEjercicio: TipoEjercicioMadurez;
  fechaEvaluacion: string;
  nivelGlobal: NivelMadurez | null;
  estado: EstadoMadurez;
  coberturaModelo: string | null;
  nivelGlobalCuestionario: NivelMadurez | null;
  coberturaCuestionario: string | null;
  observaciones: string | null;
}

export interface MadurezDimension {
  id: string;
  dimension: DimensionMadurez;
  nivel: NivelMadurez;
  color: ColorMadurez | null;
  subIndicadorValor: string | null;
  observaciones: string | null;
  // Lectura del cuestionario (percepción), independiente de la real
  nivelCuestionario: NivelMadurez | null;
  colorCuestionario: ColorMadurez | null;
  valorCuestionario: string | null;
}

export interface MadurezHeatmap {
  evaluacion: Pick<
    MadurezEvaluacion,
    | 'id'
    | 'tipoEjercicio'
    | 'estado'
    | 'nivelGlobal'
    | 'coberturaModelo'
    | 'nivelGlobalCuestionario'
    | 'coberturaCuestionario'
    | 'fechaEvaluacion'
  >;
  dimensiones: MadurezDimension[];
}

export interface MadurezSiembra {
  dimensiones: MadurezDimension[];
  nivelGlobal: NivelMadurez;
  coberturaModelo: number;
}

export interface MadurezSiembraCuestionario {
  dimensiones: MadurezDimension[];
  nivelGlobalCuestionario: NivelMadurez;
  coberturaCuestionario: number;
}

export function fetchMadurezEvaluaciones() {
  return request<{ data: MadurezEvaluacion[] }>('/structure/madurez/evaluaciones');
}

export function crearMadurezEvaluacion(body: { tipoEjercicio?: TipoEjercicioMadurez } = {}) {
  return request<{ data: MadurezEvaluacion }>('/structure/madurez/evaluaciones', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function sembrarMadurezDimensiones(id: string) {
  return request<{ data: MadurezSiembra }>(`/structure/madurez/evaluaciones/${id}/sembrar`, {
    method: 'POST',
  });
}

export function sembrarMadurezCuestionario(id: string) {
  return request<{ data: MadurezSiembraCuestionario }>(
    `/structure/madurez/evaluaciones/${id}/sembrar-cuestionario`,
    { method: 'POST' },
  );
}

export function fetchMadurezHeatmap(id: string) {
  return request<{ data: MadurezHeatmap }>(`/structure/madurez/evaluaciones/${id}/heatmap`);
}

export function aprobarMadurezEvaluacion(id: string) {
  return request<{ data: MadurezEvaluacion }>(`/structure/madurez/evaluaciones/${id}/aprobar`, {
    method: 'POST',
  });
}

export function clonarMadurezEvaluacion(id: string) {
  return request<{ data: MadurezEvaluacion }>(`/structure/madurez/evaluaciones/${id}/clonar`, {
    method: 'POST',
  });
}

export type TipoRespuestaPregunta = 'ESCALA_0_4' | 'TEXTO_LIBRE' | 'BOOLEAN';

export interface MadurezPregunta {
  id: string;
  dimension: DimensionMadurez;
  enunciado: string;
  orden: number;
  tipoRespuesta: TipoRespuestaPregunta;
  subIndicador: string | null;
}

export interface MadurezCuestionario {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  preguntas: MadurezPregunta[];
}

export interface MadurezRespuestaInput {
  preguntaId: string;
  valor?: number | null;
  valorTexto?: string | null;
  nota?: string | null;
}

export interface MadurezRespuestaGuardada {
  preguntaId: string;
  valor: number | null;
  valorTexto: string | null;
  nota: string | null;
}

export interface MadurezDiscrepancia {
  preguntaId: string;
  dimension: DimensionMadurez;
  enunciado: string;
  nRespondentes: number;
  media: number;
  varianza: number;
  discrepancia: boolean;
  valores: number[];
}

export interface MadurezDeltaDim {
  dimension: DimensionMadurez;
  nivelActual: NivelMadurez;
  nivelPrevio: NivelMadurez | null;
  delta: number | null;
}

export interface MadurezDelta {
  actual: { id: string; nivelGlobal: NivelMadurez | null; fechaEvaluacion: string };
  previa: { id: string; nivelGlobal: NivelMadurez | null; fechaEvaluacion: string } | null;
  deltaGlobal: number | null;
  porDimension: MadurezDeltaDim[];
}

export function fetchMadurezCuestionario() {
  return request<{ data: MadurezCuestionario }>('/structure/madurez/cuestionario');
}

export function guardarMadurezRespuestas(id: string, respuestas: MadurezRespuestaInput[]) {
  return request<{ data: { guardadas: number; respondenteId: string } }>(
    `/structure/madurez/evaluaciones/${id}/respuestas`,
    { method: 'POST', body: JSON.stringify({ respuestas }) },
  );
}

export function fetchMadurezRespuestas(id: string) {
  return request<{ data: MadurezRespuestaGuardada[] }>(
    `/structure/madurez/evaluaciones/${id}/respuestas`,
  );
}

export function fetchMadurezDiscrepancias(id: string) {
  return request<{ data: MadurezDiscrepancia[] }>(
    `/structure/madurez/evaluaciones/${id}/discrepancias`,
  );
}

export function fetchMadurezDelta(id: string) {
  return request<{ data: MadurezDelta }>(`/structure/madurez/evaluaciones/${id}/delta`);
}

// --- M1.5 F6: análisis IA + valoración cualitativa ---

export type EstadoAnalisisMadurez = 'BORRADOR' | 'GENERADO' | 'VALIDADO';

/** Cuadrante de la matriz PICK (0740). */
export type CuadrantePick = 'QUICK_WIN' | 'RETO' | 'POSIBLE' | 'DESCARTAR';

export interface RecomendacionAnalisis {
  dimension: DimensionMadurez;
  nivelGatillo: NivelMadurez | null;
  textoFinal: string;
  justificacion: string;
  prioridad: number;
  accionMejoraId: string | null;
  noAnclada: boolean;
  // PICK (0740): opcionales — los análisis previos no los traen.
  cuadrantePick?: CuadrantePick | null;
  tipoIntervencion?: string | null;
}

export interface SentinelFlagMadurez {
  ruleId: string;
  ruleName: string;
  category: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  message: string;
}

/** Lectura sobre la que la IA basa el análisis (espejo del enum backend). */
export type FuenteAnalisisMadurez = 'CUESTIONARIO' | 'REAL' | 'AMBAS';

/** Matriz DAFO generada por la IA (premium 0733 P1). */
export interface MatrizDafoIA {
  fortalezas: string[];
  debilidades: string[];
  oportunidades: string[];
  amenazas: string[];
}

export interface AnalisisMadurez {
  id: string;
  evaluacionId: string;
  diagnosticoNarrativo: string;
  recomendaciones: RecomendacionAnalisis[];
  fortalezas: string[] | null;
  riesgos: string[] | null;
  conclusionEjecutiva: string | null;
  matrizDafo: MatrizDafoIA | null;
  proveedor: string | null;
  tokensEntrada: number;
  tokensSalida: number;
  estado: EstadoAnalisisMadurez;
  fuente: FuenteAnalisisMadurez;
  sentinelFlags: SentinelFlagMadurez[] | null;
  validadoPor: string | null;
  validadoEn: string | null;
  esVigente: boolean;
  creadoEn: string;
}

export function generarAnalisisMadurez(
  evaluacionId: string,
  fuente: FuenteAnalisisMadurez = 'CUESTIONARIO',
) {
  return request<{ data: AnalisisMadurez }>(
    `/structure/madurez/evaluaciones/${evaluacionId}/analisis`,
    { method: 'POST', body: JSON.stringify({ fuente }) },
  );
}

export function obtenerAnalisisMadurez(evaluacionId: string) {
  return request<{ data: AnalisisMadurez | null }>(
    `/structure/madurez/evaluaciones/${evaluacionId}/analisis`,
  );
}

export function validarAnalisisMadurez(analisisId: string) {
  return request<{ data: AnalisisMadurez }>(`/structure/madurez/analisis/${analisisId}/validar`, {
    method: 'POST',
  });
}

export function actualizarObservacionesMadurez(evaluacionId: string, observaciones: string) {
  return request<{ data: MadurezEvaluacion }>(
    `/structure/madurez/evaluaciones/${evaluacionId}/observaciones`,
    { method: 'PATCH', body: JSON.stringify({ observaciones }) },
  );
}

// ── Escenarios what-if (Lewin · 0739) ─────────────────────────────────────────
export type EstadoEscenario = 'BORRADOR' | 'EVALUADO' | 'DESCARTADO';
export type TipoFuerza = 'IMPULSORA' | 'RESTRICTIVA';

export interface FuerzaCambio {
  id: string;
  tipo: TipoFuerza;
  descripcion: string;
  peso: number;
}

export interface EscenarioEstructura {
  id: string;
  nombre: string;
  descripcion: string | null;
  estado: EstadoEscenario;
  nivelActual: NivelMadurez | null;
  nivelObjetivo: NivelMadurez | null;
  ajusteCultural: string;
  score: string | null;
  viable: boolean | null;
  creadoEn: string;
  fuerzas: FuerzaCambio[];
}

export function listarEscenariosMadurez() {
  return request<{ data: EscenarioEstructura[] }>('/structure/madurez/escenarios');
}
export function crearEscenarioMadurez(body: {
  nombre: string;
  descripcion?: string | null;
  ajusteCultural?: number;
}) {
  return request<{ data: EscenarioEstructura }>('/structure/madurez/escenarios', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function agregarFuerzaMadurez(
  escenarioId: string,
  body: { tipo: TipoFuerza; descripcion: string; peso?: number },
) {
  return request<{ data: EscenarioEstructura }>(
    `/structure/madurez/escenarios/${escenarioId}/fuerzas`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}
export function eliminarFuerzaMadurez(escenarioId: string, fuerzaId: string) {
  return request<{ data: EscenarioEstructura }>(
    `/structure/madurez/escenarios/${escenarioId}/fuerzas/${fuerzaId}`,
    { method: 'DELETE' },
  );
}
export function evaluarEscenarioMadurez(escenarioId: string) {
  return request<{ data: EscenarioEstructura }>(
    `/structure/madurez/escenarios/${escenarioId}/evaluar`,
    { method: 'POST' },
  );
}

/** Descarga binaria genérica (Bearer + cookie). El filename real lo fija el backend. */
async function descargarBlobMadurez(path: string, fallbackName: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function descargarExportadorPyMadurez(analisisId: string): Promise<void> {
  return descargarBlobMadurez(
    `/structure/madurez/analisis/${analisisId}/exportador-py`,
    'generar_informe_madurez.py',
  );
}

export function descargarMegapromptMadurez(evaluacionId: string): Promise<void> {
  return descargarBlobMadurez(
    `/structure/madurez/evaluaciones/${evaluacionId}/megaprompt`,
    'megaprompt-madurez.md',
  );
}

/**
 * Obtiene el informe HTML (autónomo) de una evaluación y devuelve un object URL
 * blob listo para abrir en otra pestaña. El llamador revoca la URL cuando proceda.
 */
export async function obtenerInformeHtmlMadurez(evaluacionId: string): Promise<string> {
  const token = getToken();
  const res = await fetch(`${BASE}/structure/madurez/evaluaciones/${evaluacionId}/informe-html`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// --- Relaciones (Structure) ---

export function fetchRelaciones(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<{ data: unknown[] }>(`/relaciones${qs}`);
}

export function crearRelacion(body: {
  puestoPadreId: string;
  puestoHijoId: string;
  tipoRelacion: string;
  esPrincipal: boolean;
}) {
  return request<{ data: unknown }>('/relaciones', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function actualizarRelacion(id: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/relaciones/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function eliminarRelacion(id: string) {
  return request<void>(`/relaciones/${id}`, { method: 'DELETE' });
}

// --- Asignaciones (directas, fuera de personas) ---

export function crearAsignacion(body: {
  personaId: string;
  puestoId: string;
  fte?: number;
  esPrincipal?: boolean;
  fechaInicio: string;
}) {
  return request<{ data: unknown }>(`/apps/personas/${body.personaId}/asignaciones`, {
    method: 'POST',
    body: JSON.stringify({
      puestoId: body.puestoId,
      fte: body.fte ?? 1,
      esPrincipal: body.esPrincipal ?? true,
      fechaInicio: body.fechaInicio,
    }),
  });
}

export function finalizarAsignacion(personaId: string, asignacionId: string) {
  return request<{ data: unknown }>(`/apps/personas/${personaId}/asignaciones/${asignacionId}`, {
    method: 'DELETE',
  });
}

// --- Fichas de Caracterización ---

export interface FichaResponsable {
  puesto: { titulo: string; codigo: string | null; nivel: string };
  persona: { nombre: string; apellidos: string } | null;
}

export interface FichaDocumento {
  id: string;
  codigo: string;
  titulo: string;
  tipoDocumental: string;
  nivelDocumental: string;
  estado: string;
  direccion: string;
}

export interface FichaIndicador {
  id: string;
  nombre: string;
  descripcion: string | null;
  unidad: string | null;
  valorObjetivo: number | null;
  tipoCalculo: string;
}

export interface FichaRolGobernanza {
  id: string;
  tipoRol: string;
  ambito: string | null;
  nombrePersonalizado: string | null;
  puesto: { titulo: string; codigo: string | null };
  persona: { nombre: string; apellidos: string } | null;
}

export interface FichaData {
  proceso: {
    id: string;
    codigo: string;
    nombre: string;
    tipologia: string;
    grupoNumero: number;
    grupoNombre: string;
    fase: string | null;
    descripcion: string | null;
    activo: boolean;
    orden: number;
  };
  responsable: FichaResponsable | null;
  documentos: FichaDocumento[];
  indicadores: FichaIndicador[];
  rolesGobernanza: FichaRolGobernanza[];
}

export function fetchFichaCaracterizacion(codigo: string) {
  return request<{ data: FichaData }>(`/procesos/${codigo}/ficha`);
}

// Ficha Panel data type
export interface FichaPanelData {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string;
  alcance: string;
  responsable: { id: string; titulo: string; codigo: string } | null;
  representante: { id: string; nombre: string; apellidos: string; codigo: string } | null;
  involucrados: string[];
  entradas: { value: string; dir: string }[];
  salidas: { value: string; dir: string }[];
  reportes: string[];
  requisitos: string[];
  herramientas: string[];
  documentos: { id: string; codigo: string; titulo: string; tipoDocumental: string | null }[];
  indicadores: {
    id: string;
    nombre: string;
    descripcion: string | null;
    unidad: string;
    valorObjetivo: number | null;
    tipoCalculo: string;
  }[];
}

// Fetch ficha by process ID (used by ProcessSheetPanel)
export function fetchFicha(procesoId: string) {
  return request<{ data: FichaPanelData }>(`/procesos/${procesoId}/ficha-panel`).catch(() => ({
    data: {
      id: '',
      codigo: '',
      nombre: '',
      descripcion: '',
      alcance: '',
      responsable: null,
      representante: null,
      involucrados: [],
      entradas: [],
      salidas: [],
      reportes: [],
      requisitos: [],
      herramientas: [],
      documentos: [],
      indicadores: [],
    } as FichaPanelData,
  }));
}

// Update ficha data (used by ProcessSheetPanel)
export function actualizarFicha(procesoId: string, body: Record<string, unknown>) {
  return request<{ data: unknown }>(`/procesos/${procesoId}/ficha-panel`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// --- Importacion (Sprint 5: Document Import) ---

export async function parsearDocumento(file: File) {
  const formData = new FormData();
  formData.append('archivo', file);

  const token = getToken();
  // Multipart: Bearer manual (ver nota en subirArchivo arriba).
  const res = await fetch(`${BASE}/importacion/parsear`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    // NO Content-Type — browser sets multipart boundary
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `Error ${res.status}`);
  }

  return res.json();
}

export function confirmarImportacion(data: {
  metadatos: {
    codigo: string;
    nombre: string;
    tipoDocumental: string;
    procesoId?: string;
    direccion?: string;
  };
  secciones: unknown[];
  edicion?: number;
}) {
  return request<{
    data: { documentoId: string; codigo: string; versionId: string; edicion: number };
  }>('/importacion/confirmar', { method: 'POST', body: JSON.stringify(data) });
}

// --- Secciones y Bloques (Sprint 3: Document Editor) ---

export function fetchSecciones(versionId: string) {
  return request<{ data: import('../../../types').SeccionDocumento[] }>(
    `/versiones/${versionId}/secciones`,
  );
}

export function actualizarBloque(bloqueId: string, body: { contenido: unknown }) {
  return request<{ data: import('../../../types').BloqueContenido }>(`/bloques/${bloqueId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// --- Sugerencias VIA (Sprint 3: Document Editor) ---

export function fetchSugerencias(codigoDocumento: string) {
  return request<{ data: import('../../../types').SugerenciaContenido[] }>(
    `/documentos/${codigoDocumento}/sugerencias`,
  );
}

export function aceptarSugerencia(id: string) {
  return request<{ data: import('../../../types').SugerenciaContenido }>(
    `/sugerencias/${id}/aceptar`,
    { method: 'POST' },
  );
}

export function rechazarSugerencia(id: string) {
  return request<{ data: import('../../../types').SugerenciaContenido }>(
    `/sugerencias/${id}/rechazar`,
    { method: 'POST' },
  );
}

// --- V·IA Chat (M9/M10) ---

export function chatVIA(
  mensaje: string,
  historial?: Array<{ role: string; content: string }>,
  contexto?: { modulo: string; vista: string; entidadId?: string; extra?: string },
) {
  return request<{ data: { respuesta?: string; response?: string; proveedor?: string } }>(
    '/via/chat',
    {
      method: 'POST',
      body: JSON.stringify({ mensaje, historial, contexto }),
    },
  );
}

export function viaGenerar(data: {
  codigoDocumento: string;
  versionId?: string;
  instruccion?: string;
  tipo?: string;
}) {
  return request<unknown>('/via/generar', { method: 'POST', body: JSON.stringify(data) });
}

export function viaCorregir(data: {
  codigoDocumento: string;
  seccionId?: string;
  instruccion?: string;
}) {
  return request<unknown>('/via/corregir', { method: 'POST', body: JSON.stringify(data) });
}

export function viaAuditar(codigoDocumento: string) {
  return request<unknown>('/via/auditar', {
    method: 'POST',
    body: JSON.stringify({ codigoDocumento }),
  });
}

export function viaTraducir(data: {
  codigoDocumento: string;
  idiomaDestino: string;
  seccionId?: string;
}) {
  return request<unknown>('/via/traducir', { method: 'POST', body: JSON.stringify(data) });
}

// --- Admin API (M8) ---

const ADMIN_API = `${BASE}/admin`;

export async function adminRequest<T>(path: string, init?: RequestInit): Promise<T> {
  // Mismo patron que request() — Authorization Bearer desde localStorage
  // (fix cross-subdomain Vercel+Railway, commit 571f88dd) + credentials: include.
  // Sin este patron, todas las tabs del panel Admin (IA, Users, Tenant, Consumo,
  // RAG, Arquitectura de Procesos…) devuelven 401 silencioso en PROD.
  const token = getToken();
  const res = await fetch(`${ADMIN_API}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `Error ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// Admin response interfaces

export interface AdminUserRow {
  id: string;
  email: string;
  nombreCompleto: string;
  nombre?: string;
  rol: string;
  activo: boolean;
  creadoEn: string;
  ultimoAcceso?: string;
  ultimo?: string;
  personaId?: string | null;
  persona?: { id: string; nombre: string; apellidos: string; codigo: string } | null;
}

export interface AdminTenantConfig {
  id: string;
  nombre: string;
  slug: string;
  nombreMarca: string | null;
  logoUrl: string | null;
  logoHeaderUrl: string | null;
  faviconUrl: string | null;
  colorPrimario: string;
  colorSecundario: string;
  colorAcento: string;
  fuente: string;
  dominioPersonalizado: string | null;
  idiomaPredeterminado: string;
  zonaHoraria: string;
  plan: string;
  modulosActivos: string[];
  maxUsuarios: number;
  formatoFecha: string;
  activo: boolean;
}

export interface AdminTenantStats {
  totalUsuarios: number;
  usuariosActivos: number;
  totalDocumentos: number;
  documentosPublicados: number;
  totalProcesos: number;
  plan: string;
  modulosActivos: string[];
}

export interface AdminAssistMode {
  modo: string;
  modelo: string;
  temp?: string | number;
  temperatura?: string | number;
  tokens?: string | number;
  maxTokens?: string | number;
}

export interface AdminGlossaryTerm {
  term?: string;
  termino?: string;
  definition?: string;
  definicion?: string;
}

export interface AdminConfigIA {
  proveedorPredeterminado: string;
  proveedorTraduccionId: string | null;
  modeloGeneracion: string;
  modeloCorreccion: string;
  modeloAuditoria: string;
  modeloTraduccion: string;
  modeloChat: string;
  chatModelo?: string;
  chatModel?: string;
  apiKeyPropia: string | null;
  temperaturaDefault: number;
  maxTokensDefault: number;
  chatLimiteDiario?: number;
  chatLimit?: number;
  comportamiento?: string;
  systemPrompt?: string;
  contextoSectorial?: string;
  contexto?: string;
  assistModes?: AdminAssistMode[];
  glosario?: AdminGlossaryTerm[];
  fallbackChat?: string[];
}

export interface AdminConsumoIa {
  tokensUsados: number;
  tokensLimite: number;
  tokensGeneracion: number;
  tokensCorreccion: number;
  tokensAuditoria: number;
  tokensTraduccion: number;
  tokensChat: number;
  mes: string;
}

export interface AdminConsumoHistoricoItem {
  mes?: string;
  month?: string;
  tokensUsados?: number;
  tokensLimite?: number;
  tokensGeneracion?: number;
  tokensCorreccion?: number;
  tokensAuditoria?: number;
  tokensTraduccion?: number;
  tokensChat?: number;
  chat?: number;
  generacion?: number;
  gen?: number;
  correccion?: number;
  corr?: number;
  auditoria?: number;
  aud?: number;
  traduccion?: number;
  trad?: number;
}

export interface AdminDashboardIA {
  interacciones: number;
  tokensUsados: number;
  tokensLimite: number;
  costeTotalEstimado: number;
  latenciaMedia: number;
  tasaExito: number;
  similitudMedia: number;
  chunksMediaConsulta: number;
  docsIndexados: number;
  chunksTotal: number;
  dimensiones: number;
  proveedores: Array<{
    nombre: string;
    modelo: string;
    rol: string;
    llamadas: number;
    tokens: number;
    latencia: number;
    coste: number;
    estado: string;
    creditos: number | null;
  }>;
  consumoPorModo: Array<{ modo: string; tokens: number }>;
  actividadDiaria: Array<{ dia: string; interacciones: number; tokensMedio: number }>;
  heatmap: number[][];
}

export interface AdminProveedorModeloRaw {
  codigo?: string;
  code?: string;
  nombre?: string;
  name?: string;
  coste?: string | number;
  cost?: string | number;
}

export interface AdminProveedorRaw {
  id: string;
  nombre?: string;
  name?: string;
  tipo?: string;
  type?: string;
  hasApiKey?: boolean;
  apiKey?: string;
  keySource?: 'bd' | 'env' | null;
  activo?: boolean;
  active?: boolean;
  modelos?: AdminProveedorModeloRaw[];
  models?: AdminProveedorModeloRaw[];
  baseUrl?: string | null;
  modeloPredeterminado?: string | null;
  prioridad?: number;
  compatibleOpenAI?: boolean;
  soportaStreaming?: boolean;
  maxContextTokens?: number;
  tpmLimit?: number | null;
  rpmLimit?: number | null;
}

export interface AdminModuloState {
  code: string;
  name: string;
  description: string;
  activo: boolean;
}

// Admin Users
// 0597 Gap G: sort cross-page server-side (sortBy whitelist + sortOrder)
export function fetchAdminUsers(
  page: number = 1,
  limit: number = 20,
  opts?: {
    sortBy?: 'nombre' | 'email' | 'rol' | 'activo' | 'ultimo' | 'creado';
    sortOrder?: 'asc' | 'desc';
    rol?: string;
    q?: string;
  },
) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (opts?.sortBy) params.set('sortBy', opts.sortBy);
  if (opts?.sortOrder) params.set('sortOrder', opts.sortOrder);
  if (opts?.rol) params.set('rol', opts.rol);
  if (opts?.q) params.set('q', opts.q);
  return adminRequest<{
    data: AdminUserRow[];
    meta: { total: number; page: number; totalPages: number };
  }>(`/users?${params.toString()}`);
}

export function createAdminUser(body: { email: string; nombreCompleto: string; rol: string }) {
  return adminRequest<{ data: unknown }>('/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateAdminUser(userId: string, body: Record<string, unknown>) {
  return adminRequest<{ data: unknown }>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteAdminUser(userId: string) {
  return adminRequest<void>(`/users/${userId}`, { method: 'DELETE' });
}

// 0613 ADR-024: matriz acceso por modulo
export type NivelModuloAcceso = 'VIEWER' | 'EDITOR' | 'ADMIN';
export interface ModuloAccesoRow {
  modulo: string;
  nivel: NivelModuloAcceso;
  actualizadoEn?: string;
}
export interface ModuloAccesoResponse {
  rol: string;
  accesos: ModuloAccesoRow[];
}
export function fetchMyModuloAcceso() {
  return adminRequest<{ data: ModuloAccesoResponse }>('/users/me/modulo-acceso');
}
export function fetchUserModuloAcceso(userId: string) {
  return adminRequest<{ data: ModuloAccesoResponse }>(`/users/${userId}/modulo-acceso`);
}
export function setUserModuloAcceso(
  userId: string,
  accesos: Array<{ modulo: string; nivel: NivelModuloAcceso }>,
) {
  return adminRequest<{ data: ModuloAccesoResponse }>(`/users/${userId}/modulo-acceso`, {
    method: 'PUT',
    body: JSON.stringify({ accesos }),
  });
}

// Admin Tenant
export function fetchAdminTenant() {
  return adminRequest<{ data: AdminTenantConfig }>('/tenant');
}

export function updateAdminTenant(body: Record<string, unknown>) {
  return adminRequest<{ data: AdminTenantConfig }>('/tenant', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function fetchAdminTenantStats() {
  return adminRequest<{ data: AdminTenantStats }>('/tenant/stats');
}

// Admin IA Config
export function fetchAdminConfigIA() {
  return adminRequest<{ data: AdminConfigIA }>('/config-ia');
}

export function updateAdminConfigIA(body: Record<string, unknown>) {
  return adminRequest<{ data: AdminConfigIA }>('/config-ia', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Admin IA Dashboard (aggregated)
export function fetchAdminDashboardIA(filtros?: Record<string, string>) {
  const params = new URLSearchParams();
  if (filtros) {
    for (const [k, v] of Object.entries(filtros)) {
      if (v) params.set(k, v);
    }
  }
  const qs = params.toString();
  return adminRequest<{ data: AdminDashboardIA }>(`/dashboard-ia${qs ? '?' + qs : ''}`);
}

// Admin IA Consumption
export function fetchAdminConsumoIa() {
  return adminRequest<{ data: AdminConsumoIa }>('/consumo-ia');
}

export function fetchAdminConsumoHistorico(meses: number = 6) {
  return adminRequest<{ data: AdminConsumoHistoricoItem[] }>(
    `/consumo-ia/historico?meses=${meses}`,
  );
}

// Admin IA Providers
export function fetchAdminProveedoresIA() {
  return adminRequest<{ data: AdminProveedorRaw[] }>('/proveedores-ia');
}

// Gateway Health — snapshot último check de proveedores IA
export interface AIHealthProvider {
  tipo: string;
  nombre: string;
  ok: boolean;
  ms: number;
  error?: string;
  checkedAt: string;
}

export interface AIHealthResponse {
  ok: boolean;
  checkedAt: string | null;
  providers: AIHealthProvider[];
}

export async function fetchHealthAI(): Promise<AIHealthResponse> {
  const res = await fetch('/api/health/ai', {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `Error ${res.status}`);
  }
  return res.json();
}

export function updateAdminProveedorIa(id: string, data: Record<string, unknown>) {
  return adminRequest<{ data: unknown }>(`/proveedores-ia/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function fetchAdminProveedorApiKey(id: string) {
  return adminRequest<{ data: { apiKey: string | null } }>(`/proveedores-ia/${id}/key`);
}

export function testAdminProveedorIa(id: string, apiKey?: string) {
  return adminRequest<{ data: { ok: boolean; error?: string } }>(`/proveedores-ia/${id}/test`, {
    method: 'POST',
    body: apiKey ? JSON.stringify({ apiKey }) : undefined,
  });
}

// Admin IA Provider CRUD
export function createAdminProveedorIa(data: { nombre: string; tipo: string; apiKey?: string }) {
  return adminRequest<{ data: unknown }>('/proveedores-ia', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function deleteAdminProveedorIa(id: string) {
  return adminRequest<void>(`/proveedores-ia/${id}`, { method: 'DELETE' });
}

// Admin IA Modelo CRUD
export function createModeloIa(proveedorId: string, data: { nombre: string; coste?: number }) {
  return adminRequest<{ data: unknown }>(`/proveedores-ia/${proveedorId}/modelos`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function deleteModeloIa(proveedorId: string, modeloId: string) {
  return adminRequest<void>(`/proveedores-ia/${proveedorId}/modelos/${modeloId}`, {
    method: 'DELETE',
  });
}

// Admin RAG Status
export function fetchAdminRAGStatus() {
  return adminRequest<{
    data: { totalChunks: number; docsIndexados: number; ultimaIndexacion: string };
  }>('/rag-status');
}

// Admin RAG Reindex
export function reindexRAG(force = false) {
  return request<{ data: { ok: boolean } }>('/via/reindex', {
    method: 'POST',
    body: JSON.stringify({ force }),
  });
}

// Admin RAG History (ultimos 10 ciclos automaticos)
export interface ReindexLogEntry {
  ts: string;
  totalDocs: number;
  totalChunks: number;
  errores: number;
  timeMs: number;
}
export function fetchAdminRAGHistory() {
  return request<{ data: ReindexLogEntry[] }>('/admin/rag-history');
}

// Admin Eval RAG
export function runEvalRAG(limit?: number) {
  return request<{ data: EvalReport }>('/via/evals/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(limit ? { limit } : {}),
  });
}

export function fetchEvalHistory() {
  return request<{ data: EvalReport[] }>('/via/evals/history');
}

export interface EvalCaseResult {
  id: string;
  query: string;
  category: string;
  expectedAnswer: string;
  generatedAnswer: string;
  retrievedDocCodes: string[];
  expectedDocCodes: string[];
  scores: {
    faithfulness: number;
    relevancy: number;
    contextPrecision: number;
    contextRecall: number;
  };
  durationMs: number;
  error?: string;
}

export interface EvalReport {
  id: string;
  timestamp: string;
  tenantId: string;
  totalCases: number;
  completedCases: number;
  failedCases: number;
  avgFaithfulness: number;
  avgRelevancy: number;
  avgContextPrecision: number;
  avgContextRecall: number;
  totalDurationMs: number;
  results: EvalCaseResult[];
}

// Admin Modules
export function fetchAdminModulos() {
  return adminRequest<{ data: AdminModuloState[] }>('/modules');
}

// --- Glosario ---

export type CategoriaTermino =
  | 'INGENIERIA'
  | 'FASE'
  | 'TIPO_DOCUMENTAL'
  | 'ROL'
  | 'GENERAL'
  | 'TECNICO';

export interface TerminoGlosario {
  id: string;
  tenantId: string;
  codigo: string;
  termino: string;
  definicion: string;
  categoria: CategoriaTermino;
  sinonimos: string[];
  noTraducir: boolean;
  activo: boolean;
  idioma: string;
  creadoEn: string;
  actualizadoEn: string;
}

export interface CrearTerminoInput {
  termino: string;
  definicion: string;
  categoria: CategoriaTermino;
  sinonimos?: string[];
  noTraducir?: boolean;
  idioma?: string;
}

export type ActualizarTerminoInput = Partial<CrearTerminoInput>;

export function fetchGlosario(filtros?: { categoria?: string; buscar?: string }) {
  const params = new URLSearchParams();
  if (filtros?.categoria) params.set('categoria', filtros.categoria);
  if (filtros?.buscar) params.set('buscar', filtros.buscar);
  const qs = params.toString();
  return request<{ data: TerminoGlosario[] }>(`/glosario${qs ? `?${qs}` : ''}`);
}

export function crearTermino(input: CrearTerminoInput) {
  return request<{ data: TerminoGlosario }>('/glosario', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function actualizarTermino(id: string, input: ActualizarTerminoInput) {
  return request<{ data: TerminoGlosario }>(`/glosario/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function eliminarTermino(id: string) {
  return request<void>(`/glosario/${id}`, { method: 'DELETE' });
}

// --- Órganos de Gobierno (F2.10+F2.11+F2.12) ---

export interface EntidadLite {
  id: string;
  codigo: string;
  razonSocial: string;
  cif: string | null;
  formaJuridica: string | null;
}

export interface MiembroOrganoGobierno {
  id: string;
  tenantId: string;
  organoId: string;
  personaId: string | null;
  nombreExterno: string | null;
  cargo: string;
  fechaNombramiento: string;
  fechaCese: string | null;
  persona: { id: string; nombre: string; apellidos: string } | null;
}

export interface OrganoGobierno {
  id: string;
  tenantId: string;
  codigo: string;
  entidadId: string;
  tipoOrgano: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
  entidad: {
    id: string;
    razonSocial: string;
    codigo: string;
    formaJuridica: string | null;
  };
  miembros: MiembroOrganoGobierno[];
}

export interface CrearOrganoInput {
  entidadId: string;
  tipoOrgano: string;
  nombre: string;
  descripcion?: string;
}

export type ActualizarOrganoInput = Partial<CrearOrganoInput & { activo: boolean }>;

export interface CrearMiembroOrganoInput {
  personaId?: string;
  nombreExterno?: string;
  cargo: string;
  fechaNombramiento: string;
  fechaCese?: string;
}

export interface ActualizarMiembroOrganoInput {
  cargo?: string;
  fechaNombramiento?: string;
  fechaCese?: string;
  nombreExterno?: string;
}

export function fetchOrganosGobierno() {
  return request<{ data: OrganoGobierno[] }>('/gobernanza/organos');
}

export function fetchOrganoGobierno(id: string) {
  return request<{ data: OrganoGobierno }>(`/gobernanza/organos/${id}`);
}

export function fetchEntidadesGobernanza() {
  return request<{ data: EntidadLite[] }>('/gobernanza/entidades');
}

export function crearOrganoGobierno(input: CrearOrganoInput) {
  return request<{ data: OrganoGobierno }>('/gobernanza/organos', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function actualizarOrganoGobierno(id: string, input: ActualizarOrganoInput) {
  return request<{ data: OrganoGobierno }>(`/gobernanza/organos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function desactivarOrganoGobierno(id: string) {
  return request<void>(`/gobernanza/organos/${id}`, { method: 'DELETE' });
}

export function añadirMiembroOrgano(organoId: string, input: CrearMiembroOrganoInput) {
  return request<{ data: MiembroOrganoGobierno }>(`/gobernanza/organos/${organoId}/miembros`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function actualizarMiembroOrgano(
  organoId: string,
  miembroId: string,
  input: ActualizarMiembroOrganoInput,
) {
  return request<{ data: MiembroOrganoGobierno }>(
    `/gobernanza/organos/${organoId}/miembros/${miembroId}`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
    },
  );
}

export function cesarMiembroOrgano(organoId: string, miembroId: string) {
  return request<{ data: MiembroOrganoGobierno }>(
    `/gobernanza/organos/${organoId}/miembros/${miembroId}/cesar`,
    { method: 'POST' },
  );
}

export function eliminarMiembroOrgano(organoId: string, miembroId: string) {
  return request<void>(`/gobernanza/organos/${organoId}/miembros/${miembroId}`, {
    method: 'DELETE',
  });
}

// Admin Migration
export function fetchAdminMigracion() {
  return adminRequest<{ data: unknown }>('/migracion');
}

// =============================================================================
// 0662 — Delegacion de aprobaciones workflow (ADR-025)
// =============================================================================

export interface DelegarWorkflowBody {
  delegadoUserId: string;
  motivo: string;
  fechaFin?: string | null;
}

export interface DelegacionWorkflowItem {
  id: string;
  delegadorNombre: string | null;
  delegadoNombre: string | null;
  motivo: string;
  artefactoTipo: 'DOC' | 'FLUJOGRAMA' | 'ORGANIGRAMA' | 'PROCESO' | null;
  artefactoId: string | null;
  fechaInicio: string;
  fechaFin: string | null;
  activa: boolean;
  tipo: 'recibida' | 'realizada';
  origen: 'workflow';
}

export function delegarWorkflow(
  artefactoTipo: 'DOC' | 'FLUJOGRAMA' | 'ORGANIGRAMA' | 'PROCESO',
  artefactoId: string,
  body: DelegarWorkflowBody,
) {
  return request<{ data: { ok: boolean; delegacionId: string } }>(
    `/workflow/instancias/${artefactoTipo}/${artefactoId}/delegar`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    },
  );
}

export function fetchMisDelegacionesWorkflow() {
  return request<{ data: DelegacionWorkflowItem[] }>('/workflow/delegaciones/mis');
}

export function revocarDelegacionWorkflow(delegacionId: string) {
  return request<{ data: { ok: boolean } }>(`/workflow/delegaciones/${delegacionId}/revocar`, {
    method: 'POST',
  });
}
